import { useState, useEffect, useRef } from "react";
import { Plus, ClipboardList, Barcode } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { PriceListRow } from "@/components/PriceListRow";
import { ProcurementHistoryDialog } from "@/components/products/ProcurementHistoryDialog";
import UpcLookupDialog from "@/components/quotes/UpcLookupDialog";
import { toPriceRowCarton, toPriceRowCost } from "@/modules/order-products";

const NUMERIC_FIELDS = [
  "final_price", "loading_rate", "factory_cost", "factory_cost_usd", "case_length", "case_width", "case_height", "case_weight", "container_qty_40", "qty_per_ctn",
  "price_per_meter", "material_usage", "processing_fee", "accessory_price", "profit_tax",
];
const BLANK_ROW = {
  size: "", upc: "", price: 0, final_price: 0, loading_rate: 0, factory_cost: 0, factory_cost_usd: 0,
  case_length: 0, case_width: 0, case_height: 0, case_weight: 0, container_qty_40: 0, qty_per_ctn: 0, cost_currency: "RMB",
  price_per_meter: 0, material_usage: 0, processing_fee: 0, accessory_price: 0, profit_tax: 0,
};
const DEFAULT_WIDTHS = {
  size: 100, upc: 110, factory_cost: 180, qty_per_ctn: 60, container_qty_40: 90,
  container_qty_40_compressed: 88,
  case_length: 62, case_width: 62, case_height: 62, case_weight: 62, cbm_ctn: 75,
  display_price_usd: 80, display_price_cad: 66,
  quote_time: 90, actions: 110,
};

export function PriceListTable({ itemNumber, rows, setRows, buyerItemNumber, description = "", compression = null }) {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [pqOpen, setPqOpen] = useState(false);
  const [upcOpen, setUpcOpen] = useState(false);   // 2026-06 · UPC 查询
  const [colWidths, setColWidths] = useState(() => {
    try {
      return { ...DEFAULT_WIDTHS, ...JSON.parse(localStorage.getItem("quotesys_pl_widths") || "{}") };
    } catch {
      return DEFAULT_WIDTHS;
    }
  });
  const widthsRef = useRef(colWidths);
  widthsRef.current = colWidths;

  useEffect(() => {
    widthsRef.current = colWidths;
  }, [colWidths]);

  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthsRef.current[key];
    const onMove = (ev) => {
      const w = Math.max(45, startW + ev.clientX - startX);
      setColWidths((prev) => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      localStorage.setItem("quotesys_pl_widths", JSON.stringify(widthsRef.current));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const addRow = async () => {
    if (itemNumber) {
      try {
        const { data } = await api.post("/price-lists", { ...BLANK_ROW, item_number: itemNumber });
        setRows([...rows, data]);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } else {
      setRows([...rows, { ...BLANK_ROW }]);
    }
  };

  const updateField = (idx, field, value) => {
    const parsed = NUMERIC_FIELDS.includes(field) ? parseFloat(value) || 0 : value;
    setRows(rows.map((r, i) => (i === idx ? { ...r, [field]: parsed } : r)));
  };

  const saveRow = async (idx, override) => {
    const row = override || rows[idx];
    if (!row.id) return;
    try {
      const { data } = await api.put(`/price-lists/${row.id}`, row);
      setRows((prev) => prev.map((r, i) => (i === idx ? data : r)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeRow = async (idx) => {
    const row = rows[idx];
    if (row.id) {
      try {
        await api.delete(`/price-lists/${row.id}`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        return;
      }
    }
    setRows(rows.filter((_, i) => i !== idx));
  };

  // 2026-06 · 导入订单产品资料 → 装箱数据 (装箱率/长/宽/高/毛重/QTY 40HQ) for one row.
  const importOrderCarton = (idx, record, opts = {}) => {
    const patch = Object.fromEntries(Object.entries(toPriceRowCarton(record)).filter(([, v]) => v));
    // 历史采购价是人民币 → 写入 factory_cost 并把该行切回 RMB
    if (opts.withPrice && record.purchase_price) {
      Object.assign(patch, toPriceRowCost(record), { factory_cost_usd: 0 });
    }
    const next = { ...rows[idx], ...patch };
    setRows(rows.map((r, i) => (i === idx ? next : r)));
    if (next.id) saveRow(idx, next);
  };

  // 2026-06 · UPC 查询 writes the picked code into one row (index-addressed
  // because freshly added rows have no id yet) and persists saved rows.
  const applyUpc = (rowKey, code) => {
    const idx = Number(rowKey);
    if (Number.isNaN(idx) || !rows[idx]) return;
    const next = { ...rows[idx], upc: code };
    setRows(rows.map((r, i) => (i === idx ? next : r)));
    if (next.id) saveRow(idx, next);
  };

  const fmtTime = (dt) => (dt ? new Date(dt).toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US") : "-");

  // import selected procurement-quote records as new price list rows
  const importProcurementRows = async (mappedRows) => {
    if (!mappedRows.length) return;
    try {
      if (itemNumber) {
        const created = [];
        for (const row of mappedRows) {
          const { data } = await api.post("/price-lists", { ...BLANK_ROW, ...row, item_number: itemNumber });
          created.push(data);
        }
        setRows([...rows, ...created]);
      } else {
        setRows([...rows, ...mappedRows.map((row) => ({ ...BLANK_ROW, ...row }))]);
      }
      toast.success(t("pq.imported").replace("{n}", mappedRows.length));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // [key, label, unit, editable, type] — type "cost" renders the single factory-cost cell with ¥/US$ toggle
  const columns = [
    ["size", t("priceList.size"), null, true, "text"],
    ["upc", "UPC", null, true, "text"],
    ["factory_cost", t("priceList.factoryCost"), "¥ / US$", true, "cost"],
    ["qty_per_ctn", t("priceList.qtyPerCtn"), null, true, "number"],
    ["case_length", t("priceList.caseLength"), "cm", true, "number"],
    ["case_width", t("priceList.caseWidth"), "cm", true, "number"],
    ["case_height", t("priceList.caseHeight"), "cm", true, "number"],
    ["case_weight", t("priceList.caseWeight"), "Kg", true, "number"],
    ["cbm_ctn", "CBM/CTN", "m³", false, null],
    ["container_qty_40", t("priceList.qty40"), null, true, "number"],
    ["container_qty_40_compressed", t("priceList.qty40Compressed"), null, false, null],
    ["display_price_usd", t("priceList.usPrice"), "US$", false, null],
    ["display_price_cad", t("priceList.cadPrice"), "C$", false, null],
  ];

  const thClass = "relative border-b border-r border-border bg-secondary/60 px-2 py-1.5 text-left font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground select-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-bold tracking-tight">{t("priceList.title")}</h3>
        <div className="flex items-center gap-2">
          {/* 采购报价历史 button: previously required a filled buyer_item_number,
              which hid it for products where 采购编号 is blank. Now shown for any
              staff role — dialog falls back to itemNumber when buyer_item_number
              is empty so users can still browse & import procurement history.
              2026-02 per user report「丢了一个按钮」. */}
          {["admin", "export", "buyer", "sales_manager", "procurement_manager"].includes(user?.role) && Boolean(itemNumber || buyerItemNumber) && (
            <Button type="button" size="sm" variant="outline" onClick={() => setPqOpen(true)} data-testid="pq-history-button" className="rounded-md">
              <ClipboardList size={14} /> {t("pq.historyButton")}
            </Button>
          )}
          {/* 2026-09 · 压缩率 (产品级) — 放在 UPC 查询按钮左边 (user spec)。 */}
          {compression && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">{t("priceList.compressionRate")}</span>
              <input
                type="number" step="0.01" placeholder="1.3"
                value={compression.value ?? 0}
                onChange={(e) => compression.onChange(parseFloat(e.target.value) || 0)}
                onBlur={compression.onCommit}
                disabled={compression.disabled}
                data-testid="product-compression-rate-input"
                className="h-8 w-20 rounded-md border border-border bg-background px-2 text-xs font-mono text-right outline-none focus:border-brand disabled:opacity-50"
              />
            </div>
          )}
          <Button type="button" size="sm" variant="outline" onClick={() => setUpcOpen(true)} data-testid="price-list-open-upc" className="rounded-md">
            <Barcode size={14} /> {t("upc.button")}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-price-list-row-button" className="rounded-md">
            <Plus size={14} /> {t("priceList.addSize")}
          </Button>
        </div>
      </div>
      <div className="border border-border rounded-md overflow-x-auto">
        <table data-testid="price-list-table" style={{ tableLayout: "fixed", borderCollapse: "collapse" }}>
          <colgroup>
            {columns.map(([key]) => (
              <col key={key} style={{ width: colWidths[key] }} />
            ))}
            <col style={{ width: colWidths.quote_time }} />
            <col style={{ width: colWidths.actions }} />
          </colgroup>
          <thead>
            <tr>
              {columns.map(([key, label, unit]) => (
                <th key={key} className={thClass}>
                  <span className="block truncate">{label}</span>
                  <span className="block text-[9px] font-normal normal-case text-muted-foreground/80 h-3">{unit || ""}</span>
                  <span
                    onMouseDown={(e) => startResize(e, key)}
                    data-testid={`col-resize-${key}`}
                    className="absolute top-0 -right-1 z-10 h-full w-2.5 cursor-col-resize hover:bg-brand/50"
                  />
                </th>
              ))}
              <th className={thClass}>
                <span className="block truncate">{t("priceList.quoteTime")}</span>
                <span className="block text-[9px] h-3" />
                <span onMouseDown={(e) => startResize(e, "quote_time")} className="absolute top-0 -right-1 z-10 h-full w-2.5 cursor-col-resize hover:bg-brand/50" />
              </th>
              <th className={thClass.replace("border-r ", "")} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <PriceListRow
                key={row.id || idx}
                row={row}
                idx={idx}
                columns={columns}
                onFieldChange={updateField}
                onSaveRow={saveRow}
                onRemoveRow={removeRow}
                fmtTime={fmtTime}
                itemNumber={itemNumber}
                onImportOrderCarton={importOrderCarton}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ProcurementHistoryDialog
        open={pqOpen}
        onOpenChange={setPqOpen}
        buyerItemNumber={buyerItemNumber || itemNumber || ""}
        onImport={importProcurementRows}
      />
      <UpcLookupDialog
        open={upcOpen}
        onClose={() => setUpcOpen(false)}
        itemNumber={itemNumber || buyerItemNumber || ""}
        description={description}
        rows={rows.map((r, i) => ({ id: String(i), size: r.size, upc: r.upc }))}
        onPick={applyUpc}
      />
    </div>
  );
}
